import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct, updateProduct } from '../../services/api';
import { Product, ProductFormData } from '../../types';
import ProductForm from '../../components/products/ProductForm';
import ContentLoader from '../../components/ui/ContentLoader';

const EditProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        setError('Invalid product ID');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getProduct(id);
            // Product loaded for editing
        setProduct(data);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  const handleSubmit = async (data: ProductFormData) => {
    if (!id) return;
    
    setIsSaving(true);
    setError(null);
    
    // Submitting updated product data

    try {
      const result = await updateProduct(id, data);
              // Product updated successfully
      navigate(`/products/${id}`);
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError(err.response?.data?.error || 'Failed to update product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <ContentLoader message="Loading product..." size="lg" />
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="p-6">
        <div className="card flex flex-col items-center justify-center py-8">
          <h2 className="text-xl font-semibold mb-2">{error}</h2>
          <button 
            onClick={() => navigate('/products')} 
            className="btn-primary mt-4"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      {product && (
        <ProductForm 
          initialProduct={product} 
          onSubmit={handleSubmit} 
          isLoading={isSaving} 
        />
      )}
    </div>
  );
};

export default EditProductPage; 